import React, { Component } from 'react';
import { Alert, Button } from '@mui/material';
import Select from 'react-select';
import { FormattedMessage } from 'react-intl';

import FileUploadDialog from '../components/trial/FileUploadDialog';
import ContentBox from '../components/layout/ContentBox';
import ApiClient from '../loopback/apiClient';
import { importMultiBlockComments } from '../components/utilities/ExcelImport';
import {
  Trial, QuipUser, ReviewRequest, Institute,
} from '../redux/types';
import { convertToQid } from '../utilities/stringHelper';
import withRouter, { RouteType } from '../hoc/withRouter';

type ExtendedQuipUser = QuipUser & { institute?: Institute };
type ExtendedReviewRequest = ReviewRequest & {
  assignee: ExtendedQuipUser;
  trial: Trial;
};

type Reviewers = {
  value: number;
  label: string;
}

type LocationState = {
  trial: Trial;
}

type Props = RouteType;

type State = {
  selectedReviewer?: number;
  showUploadDialog: boolean;
  multiCommentAlreadyExists: boolean;
  showSuccess: boolean;
  requests?: ExtendedReviewRequest[];
  reviewers?: ExtendedQuipUser[];
}

class ReviewManagementView extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showSuccess: false,
      showUploadDialog: false,
      multiCommentAlreadyExists: false,
    };
  }

  componentDidMount(): void {
    window.scrollTo(0, 0); // this is a smally hack due to way the component is loaded by router
    this.fetchReviewRequestsInState();
    this.fetchReviewersInState();
  }

  fetchReviewRequestsInState = (): void => {
    const { router } = this.props;
    ApiClient.getReviewRequests((router.location.state as LocationState).trial.id)
      .then((reviewRequests) => {
        this.setState({ requests: reviewRequests });
      });
  };

  fetchReviewersInState = (): void => {
    ApiClient.getReviewers()
      .then((reviewers) => this.setState({ reviewers }));
  };

  uploadMultiCommentClickHandler = async (): Promise<void> => {
    const { router } = this.props;
    const { multiCommentAlreadyExists } = this.state;
    if (multiCommentAlreadyExists) {
      this.setState({ showUploadDialog: true });
      return;
    }
    const extendedReview = await ApiClient
      .getExtendedReview((router.location.state as LocationState).trial.id);
    if (extendedReview[0]) {
      this.setState({ multiCommentAlreadyExists: true });
      this.setState({ showSuccess: false });
    } else {
      this.setState({ showUploadDialog: true });
    }
  };

  onUploadDialogClose = (): void => {
    this.setState({ showUploadDialog: false });
  };

  onUploadMultiblockComment = async (input: HTMLInputElement | null): Promise<void> => {
    const { router } = this.props;
    const { multiCommentAlreadyExists } = this.state;
    const file = input?.files && input.files[0];
    if (!file) {
      return;
    }
    const result = await importMultiBlockComments(file);
    await ApiClient.uploadExtendedReview((router.location.state as LocationState).trial.id, result);
    if (multiCommentAlreadyExists) {
      if (router.location.state.trial.version === 2) {
        await ApiClient.deleteAllTextblockCommentsByTrialId(
          (router.location.state as LocationState).trial.id,
        );
      } else {
        await ApiClient.clearAllExtendedPayload((router.location.state as LocationState).trial.id);
      }
    }

    this.setState({ multiCommentAlreadyExists: false });
    this.setState({ showSuccess: true });
    this.onUploadDialogClose();
  };

  getReviewers = (): Reviewers[] => {
    const { reviewers, requests } = this.state && this.state;
    if (!this.state || !reviewers) {
      return [];
    }
    const reviewersData = reviewers && reviewers
      .filter((candidate) => requests && !requests
        .find((reviewRequest) => reviewRequest.assigneeId === candidate.id));
    return reviewersData.map((reviewer) => {
      const { id, userCountry } = reviewer;
      const { institute } = reviewer;
      const qId = convertToQid(id, institute ? institute.country : userCountry);
      return {
        value: id,
        label: `${reviewer.firstname} ${reviewer.surname} - ${qId}`,
      };
    });
  };

  renderAssignee = (reviewRequest: ExtendedReviewRequest): React.ReactNode => {
    const { assignee } = reviewRequest;
    const assigneeName = `${assignee.surname} ${assignee.firstname}`;
    return (
      <div
        key={`Assignment.${reviewRequest.id}`}
      >
        {reviewRequest.closed ? <s>{assigneeName}</s> : assigneeName}
        <Button
          sx={{ marginLeft: '10px' }}
          variant="contained"
          className="ml3 mb1"
          onClick={() => ApiClient.removeReviewRequest(reviewRequest)
            .then(this.fetchReviewRequestsInState)}
          disabled={reviewRequest.closed}
        >
          <FormattedMessage id="reviewmanagement.removeAssignment" />
        </Button>
        {
          reviewRequest.closed
          && (
            <Button
              variant="contained"
              className="ml3 mb1"
              onClick={() => ApiClient.restoreReviewRequest(reviewRequest)
                .then(this.fetchReviewRequestsInState)}
            >
              <FormattedMessage id="resetPassword.buttonLabel" />
            </Button>
          )
        }
      </div>
    );
  };

  renderReviewers = (): React.ReactNode => {
    const { router } = this.props;
    const { selectedReviewer } = this.state;
    return (
      <>
        <Select
          options={this.getReviewers()}
          onChange={(selection) => {
            this.setState({ selectedReviewer: (selection as Reviewers).value });
          }}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={!this.state || !selectedReviewer}
          onClick={() => selectedReviewer !== undefined && ApiClient.addReviewRequest(
            selectedReviewer, (router.location.state as LocationState).trial.id,
          ).then(this.fetchReviewRequestsInState)}
          sx={{ mt: 2, mr: 1 }}
        >
          <FormattedMessage id="reviewmanagement.addAssignment" />
        </Button>
      </>
    );
  };

  render(): React.ReactNode {
    const { router } = this.props;
    const {
      requests,
      reviewers,
      showUploadDialog,
      multiCommentAlreadyExists,
      showSuccess,
    } = this.state;
    const { state } = router.location;
    if (!state) {
      return false;
    }
    const { trial } = state as LocationState;
    return (
      <>
        {
          this.state
          && requests
          && reviewers
          && (
            <div>
              <ContentBox
                title={<FormattedMessage id="reviewmanagement.addAssignments" values={{ trial: trial.name }} />}
              >
                {
                  reviewers.length > 0 && [
                    this.renderReviewers(),
                  ]
                }
              </ContentBox>
              <ContentBox
                title={<FormattedMessage id="reviewmanagement.removeAssignments" values={{ trial: trial.name }} />}
              >
                {
                  requests.map((r: ExtendedReviewRequest) => (
                    <div
                      key={`${r.assignee.id}.${r.trial.id}.${r.id}`}
                    >
                      {this.renderAssignee(r)}
                    </div>
                  ))
                }
              </ContentBox>
              {
                trial.reviewCategoryId === 3
                && (
                  <ContentBox title={<FormattedMessage id="textblock.uploadTextBlockComments" />}>
                    {
                      multiCommentAlreadyExists
                      && (
                        <Alert sx={{ mb: '1em' }} severity="error">
                          <FormattedMessage id="reviewmanagement.multiCommentWarning" />
                        </Alert>
                      )
                    }
                    {
                      showSuccess
                      && (
                        <Alert sx={{ mb: '1em' }} severity="success">
                          <FormattedMessage id="textblock.uploadSuccess" />
                        </Alert>
                      )
                    }
                    <Button
                      variant="contained"
                      onClick={this.uploadMultiCommentClickHandler}
                    >
                      <FormattedMessage id="trialmanagement.uploadButton" />
                    </Button>
                  </ContentBox>
                )
              }
            </div>
          )
        }
        <FileUploadDialog
          isOpen={showUploadDialog}
          onClose={this.onUploadDialogClose}
          upload={this.onUploadMultiblockComment}
        />
      </>
    );
  }
}

export default withRouter(ReviewManagementView);
